import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'

// Context
import { AuthProvider } from './contexts/AuthContext'

// Layout Components
import Header from './components/Header'
import Footer from './components/Footer'
import ErrorBoundary from './components/ErrorBoundary'

// Services
import errorMonitor from './services/errorMonitor'

// Pages
import Home from './pages/Home'
import About from './pages/About'
import Services from './pages/Services'
import Portfolio from './pages/Portfolio'
import Blog from './pages/Blog'
import BlogPost from './pages/BlogPost'
import Contact from './pages/Contact'
import Login from './pages/Login'
import SignUp from './pages/SignUp'
import Account from './pages/Account'
import Admin from './pages/Admin'

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

function App() {
  // Initialize error monitoring and debug tools
  useEffect(() => {
    // Initialize error monitoring
    errorMonitor.init();
    console.log('✓ Error monitoring initialized');

    // Log environment
    console.log('Environment:', import.meta.env.MODE);
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-gray-50">
              <ScrollToTop />
              <Routes>
              {/* Auth Routes (no header/footer) */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />

              {/* Account Routes (no header/footer, has its own layout) */}
              <Route path="/account" element={<Account />} />
              <Route path="/account/programs" element={<Account />} />
              <Route path="/account/programs/:planId" element={<Account />} />
              <Route path="/account/programs/:planId/workout/:workoutId" element={<Account />} />
              <Route path="/account/programs/:planId/nutrition/:date" element={<Account />} />

              {/* Admin Route (no header/footer, has its own layout, admin-only) */}
              <Route path="/admin" element={<Admin />} />

              {/* Main Routes (with header/footer) */}
              <Route
                path="/*"
                element={
                  <>
                    <Header />
                    <main>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/services" element={<Services />} />
                        <Route path="/portfolio" element={<Portfolio />} />
                        <Route path="/blog" element={<Blog />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                        <Route path="/contact" element={<Contact />} />

                        {/* 404 Page */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </main>
                    <Footer />
                  </>
                }
              />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  )
}

// 404 Not Found Component
function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-6">
        <h1 className="text-9xl font-display font-bold text-primary-600 mb-4">404</h1>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Page Not Found</h2>
        <p className="text-xl text-gray-600 mb-8">
          Sorry, the page you're looking for doesn't exist.
        </p>
        <a
          href="/"
          className="inline-block px-8 py-4 bg-gradient-to-r from-primary-600 to-accent-500 text-white rounded-full font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
        >
          Go Back Home
        </a>
      </div>
    </div>
  )
}

export default App